'use client';

import clsx from 'clsx';
import React, {
    Suspense,
    createContext,
    useContext,
    useCallback,
    useMemo,
} from 'react';
import useIsomorphicLayoutEffect from '@/lib/hooks/isomorphic-layout-effect';
import { initSmoothScrolling } from '@/lib/utils/initSmoothScrolling';
import { gsap } from 'gsap';
import { ScrollToPlugin } from 'gsap/ScrollToPlugin';
import { usePathname, useRouter, useSearchParams } from 'next/navigation';
import {
    LOCALE_SCROLL_RESTORE_KEY,
    LocaleScrollRestoreState,
} from '@/app/lib/utils/scroll-restoration';

// 定义上下文类型
interface SmoothScrollingContextType {
    scrollTo: (to: string | HTMLElement | number) => void;
}

const SmoothScrollingContext = createContext<SmoothScrollingContextType>({
    scrollTo() {
        return;
    },
});

function SmoothScrollingSearchParamsSync({
    scrollTo,
}: Readonly<{
    scrollTo: (to: string | HTMLElement | number) => void;
}>) {
    const searchParams = useSearchParams();
    const pathname = usePathname();
    const { replace } = useRouter();
    const defaultScrollTo = searchParams.get('p');

    useIsomorphicLayoutEffect(() => {
        if (!searchParams.has('p')) {
            return;
        }

        const params = new URLSearchParams(searchParams);
        params.delete('p');

        const nextPath = params.toString()
            ? `${pathname}?${params.toString()}`
            : pathname;

        replace(nextPath, { scroll: false });

        if (defaultScrollTo) {
            scrollTo(`#${defaultScrollTo}`);
        }
    }, [defaultScrollTo, pathname, replace, scrollTo, searchParams]);

    return null;
}

function SmoothScrollingScrollRestoration() {
    const pathname = usePathname();
    const searchParams = useSearchParams();

    useIsomorphicLayoutEffect(() => {
        if (typeof window === 'undefined') {
            return;
        }

        window.history.scrollRestoration = 'manual';

        if (searchParams.has('p')) {
            return;
        }

        const restoreRaw = window.sessionStorage.getItem(
            LOCALE_SCROLL_RESTORE_KEY,
        );
        if (restoreRaw) {
            window.sessionStorage.removeItem(LOCALE_SCROLL_RESTORE_KEY);

            try {
                const restoreState = JSON.parse(
                    restoreRaw,
                ) as LocaleScrollRestoreState;
                const isFresh = Date.now() - restoreState.createdAt < 30_000;

                if (isFresh && restoreState.pathname === pathname) {
                    requestAnimationFrame(() => {
                        window.scrollTo({
                            top: restoreState.y,
                            left: 0,
                            behavior: 'auto',
                        });
                    });
                    return;
                }
            } catch (error) {
                console.warn(
                    'Failed to restore locale scroll position:',
                    error,
                );
            }
        }

        requestAnimationFrame(() => {
            window.scrollTo({
                top: 0,
                left: 0,
                behavior: 'auto',
            });
        });
    }, [pathname]);

    return null;
}

export function SmoothScrollingContextProvider({
    children,
}: Readonly<{
    children: React.ReactNode;
}>) {
    useIsomorphicLayoutEffect(() => {
        initSmoothScrolling();
        gsap.registerPlugin(ScrollToPlugin);
    }, []);

    const scrollTo = useCallback((to: string | HTMLElement | number) => {
        gsap.to(window, {
            duration: 2,
            scrollTo: to,
        });
    }, []);

    const contextValue = useMemo(
        () => ({
            scrollTo,
        }),
        [scrollTo],
    );

    return (
        <SmoothScrollingContext.Provider value={contextValue}>
            <div className={clsx('w-full', {})}>
                <Suspense fallback={null}>
                    <SmoothScrollingScrollRestoration />
                    <SmoothScrollingSearchParamsSync scrollTo={scrollTo} />
                </Suspense>
                {children}
            </div>
        </SmoothScrollingContext.Provider>
    );
}

export default function useSmoothScrollingContext() {
    return useContext(SmoothScrollingContext);
}
